import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { toast } from "sonner";
import { Eye, Send, Percent, TrendingUp } from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

// Stable refs: recharts re-mounts if props change reference between renders
const LINE_MARGIN = { top: 5, right: 10, left: 0, bottom: 0 };
const BAR_MARGIN = { top: 5, right: 10, left: 80, bottom: 0 };
const AXIS_TICK = { fontSize: 11, fontFamily: "DM Sans" };
const TOOLTIP_STYLE = { border: "2px solid #0A0A0A", borderRadius: 0, boxShadow: "4px 4px 0 #0A0A0A", fontFamily: "DM Sans" };
const LEGEND_STYLE = { fontFamily: "DM Sans", fontSize: 12 };

export default function Analytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (d) => {
    setLoading(true);
    try {
      const r = await api.get("/analytics/funnel", { params: { days: d } });
      setData(r.data);
    } catch (e) { toast.error("Failed to load analytics"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(days); }, [days]);

  const totals = data?.totals || { views: 0, submits: 0, conversion_rate: 0 };
  const daily = data?.daily || [];
  const campaigns = data?.campaigns || [];

  const chartData = useMemo(() => daily.map((d) => ({ ...d, label: d.date.slice(5) })), [daily]);
  const topCampaigns = useMemo(() => campaigns.slice(0, 8), [campaigns]);

  return (
    <AppShell>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="label-cap mb-2">// Conversion analytics</div>
          <h1 className="font-display font-black text-4xl tracking-tighter">Funnel</h1>
        </div>
        <div className="flex gap-2">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)}
              className={`px-3 py-2 border-2 border-black font-bold text-sm ${days === d ? "bg-black text-white" : "bg-white hover:bg-zinc-100"}`}
              data-testid={`range-${d}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-2 border-black mb-8">
        <Stat icon={Eye} label="Page Views" value={totals.views} testid="stat-views" />
        <Stat icon={Send} label="Submissions" value={totals.submits} testid="stat-submits" />
        <Stat icon={Percent} label="Conversion" value={`${totals.conversion_rate}%`} testid="stat-conv" />
        <Stat icon={TrendingUp} label="Active Campaigns" value={campaigns.length} testid="stat-campaigns" />
      </div>

      <div className="card-brutal mb-6 !p-5">
        <div className="font-display font-bold text-lg mb-4">Views vs Submits — last {days} days</div>
        {loading ? <div className="h-[280px] flex items-center justify-center text-zinc-500">Loading…</div> : (
          <div className="h-[280px]" data-testid="chart-time-series">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={LINE_MARGIN}>
                <CartesianGrid stroke="#E4E4E7" strokeDasharray="0" vertical={false} />
                <XAxis dataKey="label" stroke="#0A0A0A" tick={AXIS_TICK} />
                <YAxis stroke="#0A0A0A" tick={AXIS_TICK} allowDecimals={false} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Legend wrapperStyle={LEGEND_STYLE} />
                <Line type="monotone" dataKey="views" stroke="#0A0A0A" strokeWidth={2} dot={false} name="Views" />
                <Line type="monotone" dataKey="submits" stroke="#FF4F00" strokeWidth={2} dot={false} name="Submits" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="card-brutal !p-5 mb-6">
        <div className="font-display font-bold text-lg mb-4">Top campaigns by views</div>
        {topCampaigns.length === 0 ? (
          <div className="py-12 text-center text-zinc-500">No data yet. Publish a lead magnet and share its URL to start collecting.</div>
        ) : (
          <div className="h-[280px]" data-testid="chart-campaigns">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topCampaigns} layout="vertical" margin={BAR_MARGIN}>
                <CartesianGrid stroke="#E4E4E7" strokeDasharray="0" horizontal={false} />
                <XAxis type="number" stroke="#0A0A0A" tick={AXIS_TICK} allowDecimals={false} />
                <YAxis type="category" dataKey="title" stroke="#0A0A0A" tick={AXIS_TICK} width={120} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Legend wrapperStyle={LEGEND_STYLE} />
                <Bar dataKey="views" fill="#0A0A0A" name="Views" />
                <Bar dataKey="submits" fill="#FF4F00" name="Submits" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="border-2 border-black overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b-2 border-black">
            <tr>
              <th className="text-left p-3 font-display font-bold">Campaign</th>
              <th className="text-right p-3 font-display font-bold">Views</th>
              <th className="text-right p-3 font-display font-bold">Submits</th>
              <th className="text-right p-3 font-display font-bold">Conversion</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.length === 0 ? (
              <tr><td colSpan={4} className="p-8 text-center text-zinc-500">No campaigns yet.</td></tr>
            ) : campaigns.map((c) => (
              <React.Fragment key={c.lead_magnet_id}>
                <tr className="border-b border-zinc-200" data-testid={`camp-row-${c.lead_magnet_id}`}>
                  <td className="p-3 font-bold">{c.title}</td>
                  <td className="p-3 text-right">{c.views}</td>
                  <td className="p-3 text-right">{c.submits}</td>
                  <td className="p-3 text-right">
                    <span className={`px-2 py-1 text-xs font-bold border-2 border-black ${c.conversion_rate >= 30 ? "bg-[#FF4F00] text-white" : c.conversion_rate >= 10 ? "bg-yellow-100" : "bg-zinc-100"}`}>
                      {c.conversion_rate}%
                    </span>
                  </td>
                </tr>
                {c.variants && c.variants.length > 0 && c.variants.map((v) => {
                  const winner = c.variants.length > 1 && c.variants.every((other) => v === other || v.conversion_rate >= other.conversion_rate) && v.views > 0;
                  return (
                    <tr key={`${c.lead_magnet_id}-${v.variant}`} className="border-b border-zinc-100 bg-zinc-50/50" data-testid={`camp-variant-${c.lead_magnet_id}-${v.variant}`}>
                      <td className="p-3 pl-8 text-sm text-zinc-600">↳ Variant {v.variant} {winner && <span className="ml-2 px-2 py-0.5 text-xs font-bold bg-[#FF4F00] text-white border border-black">WINNER</span>}</td>
                      <td className="p-3 text-right text-sm">{v.views}</td>
                      <td className="p-3 text-right text-sm">{v.submits}</td>
                      <td className="p-3 text-right text-sm">
                        <span className="px-2 py-1 text-xs font-bold border border-black bg-white">
                          {v.conversion_rate}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </AppShell>
  );
}

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="p-6 border-r-2 last:border-r-0 border-black bg-white" data-testid={testid}>
      <div className="flex items-center gap-2 label-cap mb-2"><Icon size={14} /> {label}</div>
      <div className="font-display font-black text-4xl">{value}</div>
    </div>
  );
}
